import { memo, useEffect } from "react";
import { Modal, Button, Checkbox, Form } from "antd";
import IntlMessages from "../Message/IntlMessages";
import DisclaimerMessage from "../Message/DisclaimerMessage";
import ErrorMessageConst from "components/Message/ErrorMessageConst";

const DisclaimerModal = ({
  code,
  close,
  actions,
  state,
  title,
  message,
  isError,
  templateNo,
  choices,
  subChoices,
  renderChioces,
  renderContentFooter,
  acceptLabel,
  rejectLabel,
  onValuesChange,
  customValueOnAccept,
  disabledOkButton,
  loadingOkButton,
  useAccept,
  ...modalProps
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (!modalProps.visible) form.resetFields();
  }, [form, modalProps.visible]);

  function _onAccept(values) {
    values = customValueOnAccept ? customValueOnAccept(values) : values;

    close();
    actions?.onAccept && actions.onAccept(values);
  }

  function _onCancel() {
    close();
    if (actions?.onCancel) {
      actions.onCancel();
    }
  }

  function _onClose() {
    close();
    if (actions?.onClose) {
      actions.onClose();
      return;
    } else if (actions?.onCancel) {
      actions.onCancel();
    }
  }

  const renderCheckboxFormItem = (name, code) => (
    <Form.Item noStyle dependencies={["disabledFields"]}>
      {({ getFieldValue }) => {
        const disabledFields = getFieldValue("disabledFields") ?? [];
        const disabled = disabledFields.includes(name);
        return (
          <Form.Item name={name} className="form-with-no-explain" valuePropName="checked">
            <Checkbox disabled={disabled}>
              <span className="user-select-none">
                <DisclaimerMessage code={code} dangerous />
              </span>
            </Checkbox>
          </Form.Item>
        );
      }}
    </Form.Item>
  );

  const renderMainMessage = () => {
    if (!message) return;
    return typeof message === "string" ? (
      isError ? (
        <ErrorMessageConst code={message} dangerous />
      ) : (
        <DisclaimerMessage code={message} dangerous />
      )
    ) : (
      message
    );
  };

  const _renderChioces = () => {
    if (!choices?.length) return;

    let choiceNodes = choices.map((code) => (
      <div key={code} className="form-check mb-3">
        {renderCheckboxFormItem(code, code)}
      </div>
    ));

    return <div className="accept-checkbox mx-0 mb-0">{renderChioces ? renderChioces(choiceNodes) : choiceNodes}</div>;
  };

  const _onValuesChange = (values) => {
    if (onValuesChange) onValuesChange(values, form);
  };

  return (
    <Modal {...modalProps}>
      {modalProps.visible && (
        <Form form={form} layout="vertical" onValuesChange={_onValuesChange} onFinish={_onAccept}>
          <div className="mnc-modal-disclaimer">
            <div className="modal-mnc-head">
              <h5 className="m-0">{(title || state?.title) && <IntlMessages id={title ?? state?.title} />}</h5>
              <Button className="btn button-ktb-close" onClick={_onClose}>
                <i className="feather-icon icon-x"></i>
              </Button>
            </div>
            <div className="modal-mnc-body">
              {renderMainMessage()}
              {_renderChioces()}
              {renderContentFooter && <div className="mt-2">{renderContentFooter(form)}</div>}
            </div>
            <div className="modal-mnc-footer">
              <div className="row justify-content-center">
                <div className="col-6 pe-1 pe-md-2">
                  <div className="d-grid">
                    <Button data-logout-btn className="btn button-ktb-line" onClick={_onCancel}>
                      <IntlMessages id={rejectLabel ?? "global.cancel"} />
                    </Button>
                  </div>
                </div>
                {useAccept && (
                  <div className="col-6 ps-1 ps-md-2">
                    <div className="d-grid">
                      <Form.Item noStyle shouldUpdate className="d-grid m-0">
                        {(form) => {
                          const disabled = disabledOkButton ? disabledOkButton(form) : false;
                          const loading = loadingOkButton ? loadingOkButton(form) : false;
                          return (
                            <Button
                              htmlType="submit"
                              data-accept-btn
                              className="btn button-ktb-main"
                              disabled={disabled}
                              loading={loading}
                            >
                              <IntlMessages id={acceptLabel ?? "global.ok"} />
                              <i className="feather-icon icon-chevron-right ms-1"></i>
                            </Button>
                          );
                        }}
                      </Form.Item>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Form>
      )}
    </Modal>
  );
};

export default memo(DisclaimerModal);

DisclaimerModal.defaultProps = {
  templateNo: 1,
  useAccept: true,
  choices: [],
};
